import { Injectable } from '@angular/core';

export interface ICalendarEvent {
  title: string;
  start: Date;
  duration: number;
  end?: Date;
  address?: string;
  description?: string;
  url?: string;
}

export enum CalendarTypeEnum {
  google = 0,
  yahoo = 1,
  iCalendar = 2,
  outlook = 3,
  outlookLive = 4,
}

const MS_IN_MINUTES = 60 * 1000;
const DATE_PUNCTUATION_REGEX = /-|:|\.\d+/g;

function formatTime(date: Date): string {
  return date.toISOString().replace(DATE_PUNCTUATION_REGEX, '');
}

function endTime(event: ICalendarEvent): string {
  if (event.end) {
    return formatTime(event.end);
  }
  return formatTime(new Date(event.start.getTime() + event.duration * MS_IN_MINUTES));
}

function formatDescription(description: string): string {
  return (description || '').replace(/\\r/g, '\n').replace(/\\n/g, '\n');
}

function uid(): string {
  const s4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return (
    s4() + s4() + '-' + s4() + '-4' + s4().substr(0, 3) + '-' + s4() + '-' + s4() + s4() + s4()
  ).toLowerCase();
}

@Injectable({ providedIn: 'root' })
export class NgAddToCalendarService {
  calendarType = CalendarTypeEnum;

  getHrefFor(type: CalendarTypeEnum, event: ICalendarEvent): string {
    const start = formatTime(event.start);
    const end = endTime(event);
    const title = encodeURIComponent(event.title || '');
    const details = encodeURIComponent(formatDescription(event.description || ''));
    const location = encodeURIComponent(event.address || '');
    const url = encodeURIComponent(event.url || '');

    switch (type) {
      case CalendarTypeEnum.google:
        return (
          encodeURI(
            `https://www.google.com/calendar/render?action=TEMPLATE&dates=${start}/${end}` +
              `&location=${event.address || ''}&sprop=${event.url || ''}`
          ) +
          `&text=${title}&details=${details}`
        );
      case CalendarTypeEnum.yahoo:
        return (
          `https://calendar.yahoo.com/?v=60&view=d&type=20` +
          `&title=${title}&st=${start}&dur=${event.duration || 60}` +
          `&desc=${details}&in_loc=${location}`
        );
      case CalendarTypeEnum.outlookLive:
        return (
          `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent` +
          `&startdt=${encodeURIComponent(event.start.toISOString())}` +
          `&enddt=${encodeURIComponent(
            (event.end || new Date(event.start.getTime() + event.duration * MS_IN_MINUTES)).toISOString()
          )}` +
          `&subject=${title}&body=${details}&location=${location}`
        );
      case CalendarTypeEnum.outlook:
      case CalendarTypeEnum.iCalendar:
      default: {
        const ics = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'BEGIN:VEVENT',
          `UID:${uid()}`,
          `DTSTART:${start}`,
          `DTEND:${end}`,
          `SUMMARY:${event.title || ''}`,
          `DESCRIPTION:${formatDescription(event.description || '')}`,
          `LOCATION:${event.address || ''}`,
          `URL:${event.url || ''}`,
          'END:VEVENT',
          'END:VCALENDAR',
        ].join('\n');
        return 'data:text/calendar;charset=utf8,' + encodeURIComponent(ics);
      }
    }
  }
}
